import React, { FC, useEffect, useMemo, useState } from "react";
import { isCloud } from "../../constants/license-constants";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { EnrolmentDevice } from "../../models/EnrolmentDevices";
import { EnrolmentTask } from "../../models/EnrolmentTask";
import { TaskOption } from "../../models/TaskOption";
import { useGlobalState } from "../../Providers";
import { getErrorMessage } from "../../services/utilities/ServiceError";
import { IShowEnrollValidOptions } from "../../services/wsPluginService/plugins/base/pluginModels";
import { useDialog } from "../dialog/DialogContext";
import List from '../list/list';
import { toast } from "react-toastify";

interface Props {
    onNext: (event: any) => void;
    availableSystemDeviceIds: number[];
    pluginDevice: IShowEnrollValidOptions;
    onBack?: (event: any) => void;
    onSkip?: (event: any) => void;
    taskIdsToExclude?: number[];
}

const TasksForm: FC<Props> = ({ onNext, availableSystemDeviceIds, pluginDevice, onBack, onSkip, taskIdsToExclude = [] }) => {
    const [taskSelected, setTaskSelected] = useState(false);
    const [task, setTask] = useState('');
    const GlobalContext = useGlobalState();
    const { translate } = useLocale();
    const enrolmentTask = new EnrolmentTask(translate);
    const enrolmentDevice = new EnrolmentDevice(translate);
    const [availableEnrolmentTask, setAvailableEnrolmentTask] = useState<TaskOption[]>([
        enrolmentTask.ENROL_CARD,
        enrolmentTask.ENROL_QR_CODE,
        enrolmentTask.ENROL_MOBILE_CREDENTIAL,
        enrolmentTask.ENCODE_CARD
    ]);
    const dialog = useDialog();

    const isCloudMode = useMemo(() => {
        return isCloud(GlobalContext?.buildInfo.usage);
    }, [GlobalContext?.buildInfo.usage]);

    useEffect(() => {
        setEnrolmentTaskToShow();
    }, [availableSystemDeviceIds, pluginDevice, isCloudMode])

    const setEnrolmentTaskToShow = async () => {
        try {
             // Always start with the base options
            const tasks: TaskOption[] = [
                enrolmentTask.ENROL_CARD,
                enrolmentTask.ENROL_QR_CODE,
                enrolmentTask.ENROL_MOBILE_CREDENTIAL,
                enrolmentTask.ENCODE_CARD
            ];

            // Only add biometrics if NOT cloud mode
            if (!isCloudMode) {
                const isFingerDeviceAvailable = enrolmentDevice.getAvailablePlugins(enrolmentDevice.ENROL_FINGER_PLUGIN, pluginDevice).length !== 0 || enrolmentDevice.getSystemDevice(enrolmentDevice.ENROL_FINGER_DEVICES, availableSystemDeviceIds).length !== 0;
                const isHandDeviceAvaileble = enrolmentDevice.getAvailablePlugins(enrolmentDevice.ENROL_HAND_PLUGIN, pluginDevice).length !== 0 || enrolmentDevice.getSystemDevice(enrolmentDevice.ENROL_HAND_DEVICES, availableSystemDeviceIds).length !== 0;
                const isFaceDeviceAvailable = enrolmentDevice.getAvailablePlugins(enrolmentDevice.ENROL_FACE_PLUGIN, pluginDevice).length !== 0 || enrolmentDevice.getSystemDevice(enrolmentDevice.ENROL_FACE_DEVICES, availableSystemDeviceIds).length !== 0;

                // Has a finger enrolment device available.
                if (isFingerDeviceAvailable) {
                    tasks.push(enrolmentTask.ENROL_FINGERPRINT);
                }
                // Has a hand enrolment device available.
                if (isHandDeviceAvaileble) {
                    tasks.push(enrolmentTask.ENROL_HAND);
                }
                // Has a face enrolment device available.
                if (isFaceDeviceAvailable) {
                    tasks.push(enrolmentTask.ENROL_FACE);
                }
            }

            setAvailableEnrolmentTask(tasks);
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
    }

    const onLocalNext = (event: any) => {
        if (event.task) {
            // Task is not available. Meaning the account or device required to perform the enrolment
            // is one of the following: inactive, not installed, no plugins or device connected.
            if (!availableEnrolmentTask.some(item => item.value === event.task)) {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.noEnrolmentDeviceFound) });
            }
            // Task is available. Meaning the account or device required to perform the enrolment is available for use.
            else {
                onNext({ ...event, id: event.id, task: event.task, description: event.description });
            }
        }
    }

    const itemSelected = (item: any) => {
        const selectedItem = item.props['data-item'];
        if (selectedItem) {
            setTaskSelected(true);
            setTask(selectedItem.value);
            onLocalNext({id: selectedItem.id, task: selectedItem.value, description: selectedItem.label});
        }
    }

    const getListItems = (): { value: JSX.Element; isSelected: boolean; }[] => {

        // Hide tasks not supported by quick enrol widget
        return availableEnrolmentTask
            .filter(item => !taskIdsToExclude.includes(item.id))
            .map(item => {
                return {
                    value: (
                        <div className="flex flex-row items-center cursor-pointer select-none" data-item={item}>
                            <div id={`task-` + item.id} className="p-2">{item.label}</div>
                        </div>
                    ),
                    isSelected: item.value === task
                };
            });
    }

    return <div className="pt-3 pr-3 h-full relative">
        <List onSelect={(_, item) => itemSelected(item)} isStriped items={getListItems()} />

        <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
            { onBack && 
                <div className="flex flex-row mt-4">
                    <button
                        className="secondary secondary-inverse py-1 px-5"
                        type="button"
                        onClick={onBack}>{translate.byKey(TranslationKeys.back)}
                    </button>
                </div>
            }

            { onSkip && 
                <div className="flex flex-row mt-4 pl-3">
                    <button
                        className="secondary secondary-inverse py-1 px-5"
                        type="button"
                        onClick={onSkip}>{translate.byKey(TranslationKeys.skip)}
                    </button>
                </div>
            }
            
            <div className="flex flex-row justify-end mt-4 pl-3">
                <button
                    className={`py-1 px-5 secondary ${taskSelected ? 'secondary-enabled' : 'secondary-disabled cursor-not-allowed'}`}
                    type="button"
                    onClick={(e) => onLocalNext(e)}>{translate.byKey(TranslationKeys.next)}
                </button>
            </div>
        </div>
    </div>
}

export default TasksForm;
